import { Linking, StyleSheet, Text } from 'react-native'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { router } from 'expo-router'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { DesignSystem } from '@/src/design-system'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Button } from '@/src/common/components/Button'
import { useEffect, useState } from 'react'
import { formatNumber } from '@/src/common/utils/general'
import { routerBack } from '@/src/common/utils/routerBack'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useEvent } from 'tamagui'
import * as Hooks from '../hooks'
import * as Stores from '../stores'
import * as Navigation from '../navigation'
import * as CardApplication from '@/src/experiences/card-application'

export function RedemptionConfirmation({
  cardAccountId,
}: {
  cardAccountId: (typeof Stores.PointsForStatementCreditApi.endpoints)['GET /card/accounts/:cardAccountId']['Types']['QueryArg']['params']['cardAccountId']
}) {
  const logError = useLogError()
  const Alert = useAlert(RedemptionConfirmation.name)

  const [redeemPointsLoading, setRedeemPointsLoading] = useState<boolean>(false)

  const { pointsToRedeem, remainingPoints, creditAmount, conversionRate, idempotencyKey } =
    Hooks.usePointsToStatementCredit()

  const { redeemPointsForStatementCredit } = Hooks.useRedeemPointsForStatementCredit()

  const cardPolicyAndProcedures =
    CardApplication.Statsig.ParameterStores.useCardPolicyAndProcedures()

  const remainingPointsFinishedLoading = typeof remainingPoints === 'number'

  const alertAboutInsufficientPoints = useEvent(() => {
    if (typeof remainingPoints === 'number' && remainingPoints < 0) {
      Alert.alert(
        'Insufficient Points Balance',
        `You do not have enough points to redeem ${formatNumber(Number(pointsToRedeem ?? 0) || 0)} points.`,
        [{ text: 'OK', onPress: () => void routerBack() }],
        { cancelable: false, closeableByOverlay: false, onDismiss: () => void routerBack() },
      )
    }
  })

  useEffect(
    () => alertAboutInsufficientPoints(),
    [remainingPointsFinishedLoading, pointsToRedeem, Alert, alertAboutInsufficientPoints],
  )

  useEffect(() => {
    if (
      !pointsToRedeem ||
      !remainingPoints ||
      !creditAmount ||
      !conversionRate ||
      !idempotencyKey
    ) {
      logError(new Error('Missing required data for redemption confirmation'), {
        pointsToRedeem,
        remainingPoints,
        creditAmount,
        conversionRate,
        idempotencyKey,
      })
    }
  }, [pointsToRedeem, remainingPoints, creditAmount, conversionRate, idempotencyKey, logError])

  const handleContinue = async () => {
    if (!idempotencyKey || !pointsToRedeem) {
      logError(new Error('Statement Credit Redemption fields are required but missing'))
      Alert.alert(
        'Oops, Something Went Wrong!',
        `We couldn't process your statement credit redemption at this time. Please try again later, or contact support if the issue persists.`,
        [
          {
            text: 'Contact Support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
            style: 'default',
          },
          { text: 'Return', onPress: router.dismissAll, style: 'default' },
        ],
      )
      return
    }
    setRedeemPointsLoading(true)

    const result = await redeemPointsForStatementCredit({
      cardAccountId: cardAccountId,
      numberOfPoints: Number(pointsToRedeem),
      idempotencyKey: idempotencyKey,
    })

    if (result) {
      router.push(Navigation.PointsToStatementCreditRoutes.StatementCreditSuccessConfirmation.route)
    } else {
      logError(new Error('Statement Credit Redemption failed on submission'))
      Alert.alert(
        'Oops, Something Went Wrong!',
        `We couldn't process your statement credit redemption at this time. Please try again later, or contact support if the issue persists.`,
        [
          {
            text: 'Contact Support',
            onPress: () => void Linking.openURL('mailto:support@mesamember.com'),
            style: 'default',
          },
          { text: 'Return', onPress: router.dismissAll, style: 'default' },
        ],
      )
      setRedeemPointsLoading(false)
      return
    }
  }

  return (
    <StandaloneWrapper
      screen="/main/points-to-statement-credit/redemption-confirmation"
      title="Confirm Redemption"
      profileProps={{ menuIconProps: { color: mesaColorNames['Mesa - Brand/White #FFFFFF'] } }}
      hideBackButton={false}
    >
      <DesignSystem.PointsRedemption.PointsRedemptionConfirmationInfoCard
        pointsToRedeem={pointsToRedeem ? Number(pointsToRedeem) || 0 : 0}
        remainingPoints={remainingPoints ? Number(remainingPoints) || 0 : 0}
        creditAmount={creditAmount ? Number(creditAmount) || 0 : 0}
        conversionRate={conversionRate || ''}
      />

      <DesignSystem.Card style={styles.whiteBlock}>
        <Text style={typography['label/sm-regular']}>
          Statement credits will appear within{' '}
          <Text style={typography['label/sm-bold']}>24 hours</Text> and cannot be used to pay a
          Minimum Payment Due.
        </Text>
        <Text style={typography['label/sm-regular']}>
          Remember to pay the Minimum Payment Due on your Card by the due date.
        </Text>
        <Text
          style={[
            typography['label/sm-regular'],
            { color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] },
          ]}
          onPress={() =>
            void openInAppBrowser(
              cardPolicyAndProcedures.get(
                'cardholder_rewards_terms_of_use',
                'https://mesamember.com/legal/mesa-membership-terms-and-conditions',
              ),
              'Terms and Conditions',
            )
          }
        >
          Terms and conditions
        </Text>
      </DesignSystem.Card>

      <Button
        title="Use Points"
        onPress={() => {
          void handleContinue()
        }}
        disabled={
          redeemPointsLoading ||
          remainingPoints === 'loading' ||
          !pointsToRedeem ||
          !remainingPoints ||
          !creditAmount ||
          !conversionRate ||
          !idempotencyKey ||
          (Number(remainingPoints) || 0) < 0
        }
        loading={redeemPointsLoading}
      />
    </StandaloneWrapper>
  )
}

const styles = StyleSheet.create({
  whiteBlock: {
    borderRadius: 12,
    padding: 16,
    gap: 24,
  },
})
